import React, { useEffect, useState } from "react";
import { useUserProfile } from "../../context";
import { getOrganisationDetails } from "../../services/organizationService";
import { PlaySignalsRange } from "../../api/playAdminApi";
import { usePlaySignalsHook } from "./usePlaySignalsHook";
import {
  AllowanceCard,
  CompletionsByFamilyCard,
  EscalationCountCard,
  EvidenceCandidateCard,
  OversightRatioCard,
  ReadinessMovementCard,
  ReviewQualityCard,
  RiskFlagsCard,
  UsageCostTable,
} from "./components/SignalBlocks";
import { EvidenceExportPanel } from "./components/EvidenceExportPanel";

const RANGES: PlaySignalsRange[] = ["7d", "30d", "90d"];

/**
 * The org-admin Play section (`/play`) — HOME-ORG-ONLY BY DESIGN: the
 * backend `/admin/play/*` APIs take no organisation parameter and always
 * scope to the VIEWER's organisation, so this page offers no org selection
 * and `useOrgSwitcher` is untouched. The header names the organisation
 * being shown (fetched by the viewer's own org id) as the positive
 * org-identity control. Aggregate signals only — never staff identities.
 *
 * Production posture (mandatory): the section ships feature-gated, and when
 * the environment kill-switch is off (`play_not_enabled` 404) the page
 * renders a dormant empty-state — never an error loop.
 */
const PlaySignals: React.FC = () => {
  const { userProfile } = useUserProfile();
  const [orgName, setOrgName] = useState<string | null>(null);
  const {
    range,
    changeRange,
    signals,
    pageState,
    retry,
    exportVisibility,
    changeExportVisibility,
    exportOffset,
    setExportOffset,
    exportData,
    exportLoading,
  } = usePlaySignalsHook();

  useEffect(() => {
    let cancelled = false;
    if (userProfile?.organisation_id) {
      getOrganisationDetails(String(userProfile.organisation_id))
        .then((org) => {
          if (!cancelled) setOrgName(org.name);
        })
        .catch(() => undefined); // name is presentational; the page still works
    }
    return () => {
      cancelled = true;
    };
  }, [userProfile?.organisation_id]);

  return (
    <div className="space-y-6 p-6" data-testid="play-signals-page">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Play practice signals
          </h1>
          <p className="text-sm text-gray-500" data-testid="play-org-header">
            {orgName
              ? `Showing ${orgName} — your organisation`
              : "Showing your organisation"}
            . Aggregate practice signals only — never individual staff
            activity.
          </p>
        </div>
        <div className="flex gap-1" role="group" aria-label="Date range">
          {RANGES.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => changeRange(r)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                range === r
                  ? "bg-[rgb(var(--pd-ink))] text-white"
                  : "bg-white text-gray-600 ring-1 ring-inset ring-gray-300"
              }`}
              data-testid={`range-${r}`}
            >
              {r}
            </button>
          ))}
        </div>
      </header>

      {pageState === "loading" && (
        <p className="text-sm text-gray-400">Loading…</p>
      )}

      {pageState === "dormant" && (
        <div
          className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center"
          data-testid="play-dormant"
        >
          <h2 className="font-medium text-gray-700">
            Play isn’t switched on in this environment yet
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            When your school starts practising with Play, aggregate signals
            will appear here.
          </p>
        </div>
      )}

      {pageState === "denied" && (
        <div
          className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center"
          data-testid="play-denied"
        >
          <h2 className="font-medium text-gray-700">
            Play isn’t part of your organisation’s package
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Talk to us about adding the practice environment.
          </p>
        </div>
      )}

      {pageState === "error" && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-800">
            Something went wrong loading the signals.
          </p>
          <button
            type="button"
            onClick={retry}
            className="mt-2 rounded-md bg-red-700 px-3 py-1.5 text-sm font-medium text-white"
          >
            Try again
          </button>
        </div>
      )}

      {pageState === "ready" && signals && (
        <>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            <OversightRatioCard value={signals.humanOversightRatio} />
            <EscalationCountCard value={signals.safetyEscalationCount} />
            <EvidenceCandidateCard value={signals.evidenceCandidateCount} />
            <CompletionsByFamilyCard data={signals.completionsByFamily} />
            <RiskFlagsCard data={signals.commonRiskFlags} />
            <ReviewQualityCard data={signals.reviewQualityDistribution} />
            <ReadinessMovementCard data={signals.readinessMovement} />
            <AllowanceCard data={signals.allowance} />
          </div>
          <UsageCostTable data={signals.usageCostByFamily} />
          <EvidenceExportPanel
            visibility={exportVisibility}
            onVisibilityChange={changeExportVisibility}
            offset={exportOffset}
            onOffsetChange={setExportOffset}
            data={exportData}
            loading={exportLoading}
          />
        </>
      )}
    </div>
  );
};

export default PlaySignals;
